import { useForm } from 'react-hook-form';
import { classNames, cleanNum, regex } from '../../utils/common';
import Multiselect from '../Common/Multiselect';
import ContactInlineSelectorField from '../Common/ContactInlineSelectorField';
import PhoneIcon from '../Common/Icons/PhoneIcon';
import SelectCurrency from './elements/SelectCurrency';
import { yupResolver } from '@hookform/resolvers/yup';
import { rechargeMovilSchema, rechargeNautaSchema } from '../../utils/schemas';
import queryContacts from '../../services/contacts/queryContacts';
import { useMemo, useState } from 'react';
import useAuthContext from '../../hooks/useAuthContext';
import PropTypes from 'prop-types';
import LoginModal from '../Auth/LoginModal';
import { useDisclosure } from '@chakra-ui/react';

const SelectBeneficiary = ({ type, setRecharge }) => {
  /* Hooks */
  const movilForm = useForm({
    resolver: yupResolver(rechargeMovilSchema),
    defaultValues: {
      currency: 'eur',
      contacts: [],
    },
  });
  const nautaForm = useForm({
    resolver: yupResolver(rechargeNautaSchema),
    defaultValues: {
      currency: 'eur',
      contacts: [],
    },
  });
  const { isAuthenticated } = useAuthContext();
  const {
    isOpen: isOpenLogin,
    onOpen: onOpenLogin,
    onClose: onCloseLogin,
  } = useDisclosure();

  /* Queries */
  const { data: contacts, isLoading: isLoadingContacts } =
    queryContacts().getContactList();

  /* States */
  const [resetMultiSelect, setResetMultiSelect] = useState(false);

  /* Memo */
  const optionsContacts = useMemo(
    () =>
      contacts
        ?.filter((c) =>
          type === 'movil'
            ? c.phone && cleanNum(c.phone)
            : c.email && regex.nauta.test(c.email),
        )
        ?.map((contact) => {
          const phone = contact.phone && cleanNum(contact.phone);
          return {
            name: contact.name,
            value: type === 'movil' ? phone : contact.email,
            phone,
            email: contact.email,
          };
        })
        .concat(
          type === 'movil'
            ? movilForm
                .watch('contacts')
                .filter(
                  (item) =>
                    !contacts?.some((contact) => contact.phone === item.phone),
                )
                .map((item) => ({
                  name: item.name,
                  value: item.phone,
                  phone: item.phone,
                }))
            : nautaForm
                .watch('contacts')
                .filter(
                  (item) =>
                    !contacts?.some((contact) => contact.email === item.email),
                )
                .map((item) => ({
                  name: item.name,
                  value: item.email,
                  email: item.email,
                })),
        ) || [],
    [contacts, type],
  );

  /* Function */
  const handleMultiselectOnChange = (data) => {
    if (type === 'movil') {
      return movilForm.setValue(
        'contacts',
        data.map((item) => ({
          phone: item.value,
          name: item.name,
          value: item.value,
        })),
      );
    }
    return nautaForm.setValue(
      'contacts',
      data.map((item) => ({
        email: item.value,
        name: item.name,
        value: item.value,
      })),
    );
  };

  const handleOnChangeCurrency = (e) => {
    if (type === 'movil') return movilForm.setValue('currency', e.value)
    return nautaForm.setValue('currency', e.value)
  };

  const onSubmit = (e) => {
    const data = {
      contacts: e.contacts,
      currency: e?.currency,
    };

    setRecharge(data);

    // reset forms
    movilForm.reset();
    nautaForm.reset();
    setResetMultiSelect(true);
  };

  return (
    <form
      onSubmit={
        type === 'movil'
          ? movilForm.handleSubmit(onSubmit)
          : nautaForm.handleSubmit(onSubmit)
      }
      className="flex flex-col gap-4 max-w-xs min-w-[250px]"
    >
      {/* Header */}
      <h2 className="text-blue-primary font-medium text-center text-2xl">
        Selecciona o escribe a quien(es) deseas beneficiar
      </h2>
      
      {/* Movil/Nauta */}
      <div className={classNames(type === 'movil' ? 'block' : 'hidden')}>
        <div className="text-blue-primary font-medium text-center mt-4 mb-2">
          Número a recargar
        </div>

        <ContactInlineSelectorField
          form={movilForm}
          type="movil"
          isLoading={isLoadingContacts}
          options={optionsContacts}
          placeholder="Ej: +53 5XXX XXXX"
          icon={<PhoneIcon w="14px" h="24px" />}
        />
      </div>

      <div className={classNames(type !== 'movil' ? 'block' : 'hidden')}>
        <div className="text-blue-primary font-medium text-center mt-4 mb-2">
          Nauta a recargar
        </div>
        <Multiselect
          type={'email'}
          isLoading={isLoadingContacts}
          listOptions={optionsContacts}
          onChange={handleMultiselectOnChange}
          reset={{
            value: resetMultiSelect,
            setValue: setResetMultiSelect,
          }}
        />
        {(nautaForm.formState.errors.contacts &&
          (Array.isArray(nautaForm.formState.errors.contacts) ? (
            nautaForm.formState.errors.contacts.map((contactError, key) => (
              <span
                key={`error-movilContact-${key}`}
                className="text-red-500 text-sm"
              >
                {`${contactError.email.message} (${
                  nautaForm.watch('contacts')[key]?.email
                })`}
              </span>
            ))
          ) : (
            <span className="text-red-500 text-sm">
              {nautaForm.formState.errors.contacts.message}
            </span>
          ))) ||
          ''}
      </div>

      {/* Select Currency */}
      <div className="flex flex-col">
        <div className="text-blue-primary font-medium text-center">
          Seleccione el tipo de moneda
        </div>
        <SelectCurrency onChange={handleOnChangeCurrency} />
      </div>

      {/* Button */}
      <div className="text-center mt-0 flex justify-center">
        {!isAuthenticated ? (
          <button
            type="button"
            className="h-auto p-2 px-10 text-md lg:text-xl font-normal rounded-full text-white bg-bubble-gum disabled:bg-gray-400 whitespace-nowrap"
            onClick={onOpenLogin}
          >
            Inicia sesión para recargar
          </button>
        ) : (
          <button
            type="submit"
            className={classNames(
              'bg-bubble-gum text-white',
              'h-auto p-2 px-10 text-md lg:text-xl font-normal rounded-full  disabled:bg-gray-400 flex items-center gap-3',
            )}
          >
            Recargar
          </button>
        )}
      </div>
      
      <LoginModal show={isOpenLogin} onClose={onCloseLogin} />
    </form>
  );
};

SelectBeneficiary.propTypes = {
  tier: PropTypes.shape({
    rechargeable_type: PropTypes.string,
    rechargeable_id: PropTypes.number,
  }),
  type: PropTypes.string,
  setRecharge: PropTypes.func,
};

export default SelectBeneficiary;
